import { useEffect, useState } from "react";
import api, { fmtMXNFull, todayLocal } from "@/lib/api";
import { X, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
  { value: "fondo_mecanico", label: "Fondo entregado al mecánico" },
  { value: "dinero_mecanico", label: "Dinero del mecánico (su bolsa)" },
  { value: "empresa", label: "Pagó la empresa" },
];

export default function QuoteToExpensesModal({ quote, onClose, onDone }) {
  const items = quote.items || [];
  const [converted, setConverted] = useState(null);
  const [checked, setChecked] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [f, setF] = useState({ paid_source: "fondo_mecanico", mechanic: "", expense_date: todayLocal() });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/workshop/quotes/${quote.id}/converted-items`).then(({ data }) => {
      setConverted(data.converted);
      setChecked(items.map((_, i) => i).filter((i) => !data.converted.includes(i)));
    }).catch(() => setConverted([]));
    api.get("/workshop/mechanics").then(({ data }) => setMechanics(data.filter((m) => m.active !== false))).catch(() => {});
  }, [quote.id]); // eslint-disable-line

  const toggle = (i) => setChecked((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  const amount = (it) => Number(it.qty || 1) * Number(it.price || 0);
  const totalSel = checked.reduce((s, i) => s + amount(items[i]), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (checked.length === 0) { toast.error("Selecciona al menos un concepto"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/workshop/quotes/${quote.id}/items-to-expenses`, {
        item_indexes: checked, paid_source: f.paid_source,
        mechanic: f.mechanic.trim() || null, expense_date: f.expense_date,
      });
      toast.success(`${data.created} gasto(s) creados${data.skipped ? ` · ${data.skipped} ya existían` : ""}`);
      onDone?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al convertir"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onSubmit={submit} className="panel-strong w-full max-w-lg overflow-y-auto" style={{ maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()} data-testid="quote-to-expenses-modal">
        <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="label-mono">// {quote.folio}</div>
            <h2 className="font-display font-bold text-xl text-slate-100 flex items-center gap-2"><Receipt className="w-5 h-5" style={{ color: "var(--cyan)" }} /> Convertir a gastos</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {converted === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>
          ) : (
            <div className="space-y-1.5">
              {items.map((it, i) => {
                const done = converted.includes(i);
                return (
                  <label key={i} data-testid={`convert-item-${i}`}
                    className={`panel p-2.5 flex items-center gap-3 ${done ? "opacity-50" : "cursor-pointer row-hover"}`}>
                    <input type="checkbox" disabled={done} checked={checked.includes(i)} onChange={() => toggle(i)} className="accent-cyan-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-200 truncate">{it.concept}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{it.qty || 1} {it.unit || "PZA"} × {fmtMXNFull(it.price)}</div>
                    </div>
                    {done ? (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest shrink-0" style={{ color: "var(--green)", border: "1px solid var(--green)" }}>YA CONVERTIDO</span>
                    ) : (
                      <span className="font-mono text-sm text-slate-100 shrink-0">{fmtMXNFull(amount(it))}</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono block mb-1.5">¿Quién pagó?</label>
              <select value={f.paid_source} onChange={(e) => setF({ ...f, paid_source: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="convert-source">
                {SOURCES.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fecha del gasto</label>
              <input type="date" required value={f.expense_date} onChange={(e) => setF({ ...f, expense_date: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="convert-date" />
            </div>
          </div>
          {f.paid_source !== "empresa" && (
            <div>
              <label className="label-mono block mb-1.5">Mecánico *</label>
              <select required value={f.mechanic} onChange={(e) => setF({ ...f, mechanic: e.target.value })}
                className="w-full input-cyber px-3 py-2 text-sm" data-testid="convert-mechanic">
                <option value="" style={{ background: "#0a0f1d" }}>— Selecciona un mecánico —</option>
                {mechanics.map((m) => (
                  <option key={m.id} value={m.name} style={{ background: "#0a0f1d" }}>{m.name}{m.workshop_name ? ` · ${m.workshop_name}` : ""}</option>
                ))}
              </select>
              {mechanics.length === 0 && <div className="text-[10px] mt-1" style={{ color: "#fbbf24" }}>No hay mecánicos registrados — regístralos en Taller → Mecánicos.</div>}
            </div>
          )}

          <div className="flex items-center justify-between pt-3" style={{ borderTop: "1px solid var(--border)" }}>
            <div className="text-xs text-slate-400">{checked.length} conceptos · <span className="font-mono" style={{ color: "var(--cyan)" }}>{fmtMXNFull(totalSel)}</span></div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving || checked.length === 0} data-testid="convert-submit"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                {saving ? "Creando…" : "Crear gastos"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
